import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Dialog, IconButton, InputBase, Typography } from '@mui/material';
import {ApartmentRounded, Close, Search} from '@mui/icons-material';
import OrganizationListRow from './OrganizationListRow';

const listScrollbarSx = {
    maxHeight: 360,
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(0,0,0,0.2) transparent',
    '&::-webkit-scrollbar': {
        width: 6,
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0,0,0,0.2)',
        borderRadius: 8,
    },
};

const OrganizationSelectModal = ({
                                     open = true,
                                     organizations = [],
                                     selectedOrganizationId,
                                     loading = false,
                                     submitting = false,
                                     error = null,
                                     onSelectOrganization,
                                     onClose,
                                     emptyMessage = 'No organizations available.',
                                     showClose = true,
                                     showSelectedHighlight = true,
                                     maxVisibleWithoutSearch = 5,
                                    switchingOrganization = false
                                 }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef(null);

    useEffect(() => {
        if (!open) {
            setSearchQuery('');
        }
    }, [open]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const focusTimer = setTimeout(() => {
            searchInputRef.current?.focus();
        }, 100);

        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && showClose) {
                onClose?.();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            clearTimeout(focusTimer);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose, open, showClose]);

    const filteredOrganizations = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        const filtered = !query
            ? organizations
            : organizations.filter((organization) => {
                const nameMatch = organization.name?.toLowerCase().includes(query);

                return nameMatch ;
            });

        return query ? filtered : filtered.slice(0, maxVisibleWithoutSearch);
    }, [organizations, searchQuery, maxVisibleWithoutSearch]);

    const hasMoreWithoutSearch = !searchQuery.trim() && organizations.length > maxVisibleWithoutSearch;
    const showEmptySearchResult = !loading && !error && searchQuery.trim() && filteredOrganizations.length === 0;
    const showEmptyList = !loading && !error && !searchQuery.trim() && organizations.length === 0;

    const handleSelect = (organizationId) => {
        if (loading || submitting || !organizationId) {
            return;
        }

        onSelectOrganization?.(organizationId);
    };

    const handleCloseClick = () => {
        onClose?.();
    };

    return (
        <Dialog
            open={open}
            onClose={showClose ? onClose : undefined}
            fullWidth={false}
            maxWidth={false}
            className="backdrop-model"
            slotProps={{
                paper: {
                    sx: {
                        width: 440,
                        maxWidth: '90vw',
                        borderRadius: '12px',
                        boxShadow: 24,
                        overflow: 'hidden'
                    },
                },
            }}
        >
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                px: 2,
                pt:2,
                pb:0.5,
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap:0.75
                }}>
                    <ApartmentRounded
                        sx={{
                            fontSize: 18,
                            color: 'text.secondary',
                        }}
                    />
                    <Typography    sx={{
                        fontSize: 18,
                        fontWeight: 600,
                        lineHeight: 1.3,
                        color: 'text.primary',
                    }}>
                        {switchingOrganization ? "Switch Organization" : "Choose an organization"}
                    </Typography>
                </Box>

                {showClose ? (
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseClick}
                        disabled={submitting}
                        size="small"
                        sx={{
                            flexShrink: 0,
                            p: 0.5,
                            color: 'action.active',
                        }}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                ) : null}
            </Box>
            {!switchingOrganization && <Typography sx={{
                fontSize: 14,
                fontWeight: 400,
                lineHeight: 1.4,
                color: 'text.secondary',
                px:2,
                pb:1.5
            }}>Select the organization you want to access.</Typography> }
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    mx: 2,
                    px:1,
                    py:0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2
                }}
            >
                <Search fontSize="small" sx={{ color: 'action.active', flexShrink: 0 }} />
                <InputBase
                    inputRef={searchInputRef}
                    fullWidth
                    placeholder={'Search by company name...'}
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    disabled={submitting}
                    sx={{
                        flex: 1,
                        fontSize: '0.875rem',
                        lineHeight: 1.5,
                        color: 'text.primary',
                        '& input::placeholder': {
                            color: 'text.secondary',
                            opacity: 1,
                        },
                    }}
                />

            </Box>

            <Box sx={{ px: 2, pt: 1.5, pb: 1.5 }}>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : null}

                {!loading && error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : null}

                {showEmptySearchResult || showEmptyList ? <Alert severity="warning">{emptyMessage}</Alert> : null}

                {!loading && !error && filteredOrganizations.length > 0 ? (
                    <Box
                        sx={{
                            ...listScrollbarSx,
                            mx: -0.5,
                            px: 0.5
                        }}
                    >
                        {filteredOrganizations.map((organization, index) => (
                            <OrganizationListRow
                                key={organization.id}
                                index={index}
                                organization={organization}
                                selected={showSelectedHighlight && organization.id === selectedOrganizationId}
                                disabled={submitting}
                                onClick={() => handleSelect(organization.id)}
                            />
                        ))}
                    </Box>
                ) : null}

                {hasMoreWithoutSearch ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        { 'Search to find more organizations'}
                    </Typography>
                ) : null}
            </Box>
        </Dialog>
    );
};

export default OrganizationSelectModal;